const STORAGE_KEY = 'voicequote-contractor';
const INVOICE_COUNTER_KEY = 'voicequote-invoice-counter';
const LOGO_STORAGE_KEY = 'voicequote-contractor-logo';
const MAX_LOGO_WIDTH = 400;

export interface ContractorSettings {
  companyName: string;
  address: string;
  regCode: string;
  vatNumber: string;
  iban: string;
  markupPercent: number;
  vatRate: number;
  paymentTermDays: number;
  defaultTerms: string;
  validityDays: number;
}

const DEFAULT_SETTINGS: ContractorSettings = {
  companyName: '',
  address: '',
  regCode: '',
  vatNumber: '',
  iban: '',
  markupPercent: 0,
  vatRate: 24,
  paymentTermDays: 14,
  defaultTerms: '',
  validityDays: 30,
};

export function loadContractorSettings(): ContractorSettings {
  try {
    const stored = globalThis.localStorage.getItem(STORAGE_KEY);
    if (!stored) return { ...DEFAULT_SETTINGS };
    return { ...DEFAULT_SETTINGS, ...JSON.parse(stored) };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

export function saveContractorSettings(settings: ContractorSettings): void {
  globalThis.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
}

export function getNextInvoiceNumber(): string {
  try {
    const current = globalThis.localStorage.getItem(INVOICE_COUNTER_KEY);
    const parsed = current ? Number.parseInt(current, 10) : 0;
    const next = Number.isNaN(parsed) ? 1 : parsed + 1;
    globalThis.localStorage.setItem(INVOICE_COUNTER_KEY, String(next));
    return String(next).padStart(4, '0');
  } catch {
    return '0001';
  }
}

export function getCurrentInvoiceCounter(): number {
  try {
    const current = globalThis.localStorage.getItem(INVOICE_COUNTER_KEY);
    if (!current) return 0;
    const parsed = Number.parseInt(current, 10);
    return Number.isNaN(parsed) ? 0 : parsed;
  } catch {
    return 0;
  }
}

export function loadLogo(): string | null {
  try {
    return globalThis.localStorage.getItem(LOGO_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function saveLogo(dataUrl: string): void {
  globalThis.localStorage.setItem(LOGO_STORAGE_KEY, dataUrl);
}

export function removeLogo(): void {
  globalThis.localStorage.removeItem(LOGO_STORAGE_KEY);
}

export function resizeImage(dataUrl: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let { width, height } = img;

      if (width > MAX_LOGO_WIDTH) {
        height = Math.round((height * MAX_LOGO_WIDTH) / width);
        width = MAX_LOGO_WIDTH;
      }

      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0, width, height);

      const isJpeg = dataUrl.startsWith('data:image/jpeg');
      resolve(isJpeg ? canvas.toDataURL('image/jpeg', 0.9) : canvas.toDataURL('image/png'));
    };
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = dataUrl;
  });
}
